@if (titleTranslationKey) {
  <app-control-header
    [label]="titleTranslationKey"
    [index]="index"
    [required]="required"
    [infoTooltip]="titleInfoTooltip"
    [infoTooltipPlacement]="titleInfoTooltipPlacement"
    [disabled]="isDisabled"
  ></app-control-header>
}

<div
  #trigger="cdkOverlayOrigin"
  cdkOverlayOrigin
  [ngClass]="{
    'opacity-50': isDisabled && !disabledFilterState,
    'opacity-60': isDisabled && disabledFilterState,
  }"
  [ngbTooltip]="controlTooltip"
  [placement]="controlTooltipPlacement"
  (click)="toggleDropdown()"
>
  @if (customTrigger) {
    <span data-test-id="dropdown-trigger">
      <ng-container *ngTemplateOutlet="customTrigger"></ng-container>
    </span>
  } @else {
    @if (dropdownButtonType === 'dropdown-input') {
      <app-dropdown-button
        [class.border-error]="errorsDisplayed"
        [isOpen]="isDropdownOpened"
        [placeholder]="placeholderTranslationKey | translate"
        [value]="dropdownButtonViewTitle ?? selectedItemsString"
        [disabled]="isDisabled$ | async"
        [isLoading]="loading"
        [disabledFilterState]="disabledFilterState"
      ></app-dropdown-button>
    } @else {
      <app-new-button
        [class.border-error]="errorsDisplayed"
        [isActive]="isDropdownOpened || !!selectedItems.length"
        [type]="'basic'"
        [size]="'responsive'"
        [disabled]="isDisabled$ | async"
      >
        {{ dropdownButtonViewTitle }}
      </app-new-button>
    }
  }
</div>

@if (displaySelectedItemsList) {
  <div class="selected-items-list" [class.hidden]="!selectedItems.length">
    @for (item of selectedItems; track $index) {
      <div class="selected-item font-main text-lg">
        <span>{{ getDisplayValue(item) }}</span>
        <svg-icon src="chip-remove" preserveAspectRatio="none" [stretch]="true" (click)="uncheckItem(item)"></svg-icon>
      </div>
    }
  </div>
}

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="trigger"
  [cdkConnectedOverlayPositions]="dropdownPosition || responsiveOverlayPositions"
  [cdkConnectedOverlayOpen]="isDropdownOpened"
  [cdkConnectedOverlayHasBackdrop]="enableBackdrop"
  [cdkConnectedOverlayBackdropClass]="enableBackdrop ? 'cdk-overlay-transparent-backdrop' : ''"
  (backdropClick)="enableBackdrop ? close() : null"
>
  <app-dropdown-options-backdrop
    #openableElement
    class="relative"
    [style.width.px]="listWidth"
    [style.max-width.px]="maxListWidthPx"
  >
    @if (searchEnabled) {
      <app-text-field
        data-test-id="search-field"
        [class]="searchFieldWith + ' mb-px'"
        [formControl]="searchField"
        [clearButtonEnabled]="true"
        [placeholderIcon]="'search'"
        [autoFocus]="searchAutoFocus"
        [placeholder]="searchFieldPlaceholder | translate"
        (click)="searchFieldClick($event)"
      ></app-text-field>
      @if (!displayedData?.length && !hideEmptyListPlaceholder) {
        <div
          data-test-id="no-search-results"
          class="min-h-10 pl-2 font-main text-base text-navy-70 flex items-center truncate"
        >
          {{ 'core.multiDropdown.emptyListPlaceholder' | translate }}
        </div>
      }
    }

    <perfect-scrollbar #parentScrollRef [style.max-height.px]="listMaxHeight" [config]="{ suppressScrollX: true }">
      @if (selectAllEnabled && selectAllPosition === 'default') {
        <app-dropdown-option class="p-0" [style.height.px]="dropdownOptionHeight">
          <app-checkbox
            [valueLabel]="'core.multiDropdown.selectAll' | translate"
            [(ngModel)]="areAllSelected"
            (changeManually)="selectAll()"
          ></app-checkbox>
        </app-dropdown-option>
      }

      @if (firstOptionSticky && displayedData?.[0]) {
        <app-dropdown-option
          *ngVar="displayedData[0] as item"
          class="p-0"
          [style.height.px]="dropdownOptionHeight"
          [class.h-auto]="doesOptionShouldBeShownFully"
          [class.sticky-option]="firstOptionSticky && !searchValue"
        >
          <app-checkbox
            [ngModel]="dictionary[getItemId(item)]"
            [valueLabel]="checkboxValueLabel(item)"
            [attr.data-test-id]="getDisplayValue(item)"
            [partiallyChecked]="partiallyCheckedDictionary[getItemId(item)]"
            (changeManually)="updateItem(item, $event.checked); changeCheckboxManually(item, $event)"
          >
            <ng-container *ngTemplateOutlet="displayValueTemplate; context: { item }"></ng-container>
          </app-checkbox>
        </app-dropdown-option>
      }

      <virtual-scroller
        #scroller
        [parentScroll]="$any(parentScrollRef)?.directiveRef?.elementRef.nativeElement"
        [bufferAmount]="30"
        [items]="scrollableItems"
        [enableUnequalChildrenSizes]="true"
      >
        @for (item of scroller.viewPortItems; track menuActionTrackBy(index, item); let index = $index) {
          <app-dropdown-option
            class="p-0"
            [style.height.px]="dropdownOptionHeight"
            [class.h-auto]="doesOptionShouldBeShownFully"
          >
            <app-checkbox
              [ngModel]="dictionary[getItemId(item)]"
              [valueLabel]="checkboxValueLabel(item)"
              [attr.data-test-id]="getDisplayValue(item)"
              [partiallyChecked]="partiallyCheckedDictionary[getItemId(item)]"
              [isReadOnly]="isItemReadOnly(item)"
              (changeManually)="updateItem(item, $event.checked); changeCheckboxManually(item, $event)"
            >
              <ng-container *ngTemplateOutlet="displayValueTemplate; context: { item }"></ng-container>
            </app-checkbox>
          </app-dropdown-option>
        }
      </virtual-scroller>
    </perfect-scrollbar>

    @if (isBottomOptionShown) {
      <app-dropdown-option
        class="border-t border-navy-30"
        [context]="searchField.value"
        [style.height.px]="dropdownOptionHeight"
        [menuAction]="bottomDropdownAction"
      ></app-dropdown-option>
    }

    @if (underlineActions?.length || isSelectAllUnderlineAction) {
      <div data-test-id="underline-actions-divider" class="border-navy-30 border-t"></div>
      @for (underlineAction of underlineActions; track menuActionTrackBy($index, underlineAction)) {
        <app-dropdown-option
          [attr.data-test-id]="'underline-action-' + underlineAction.id"
          [menuAction]="underlineAction"
        ></app-dropdown-option>
      }

      @if (isSelectAllUnderlineAction) {
        <app-dropdown-option
          [attr.data-test-id]="'underline-action-select-all'"
          [context]="areAllSelected"
          [menuAction]="selectAllUnderlineAction"
        ></app-dropdown-option>
      }
    }
  </app-dropdown-options-backdrop>
</ng-template>

@if (errorsDisplayed) {
  <app-control-errors class="flex-1" [errorTexts]="errorTexts"></app-control-errors>
}
